<script>
  import { onMount, onDestroy } from 'svelte';
  import { navigate as goto } from 'svelte-routing';
  import { page } from '$stores/pageStore';
  import StudyDetails from '$lib/StudyDetails.svelte';
  import FeatureInfo from '$lib/FeatureInfo.svelte';
  import { selectedStudy, selectedSite, selectedParticipant } from '$stores/selectionStore';
  import { isAuthenticated } from '$stores/sessionStore';
  import { studyProperties, fetchStudyProperties } from '$stores/configStore';
  import { glucoseunit } from '$stores/unitsStore';
  import { convertGlucoseValue } from '$utils/unitUtils';
  import { exportSensorStatisticsToExcel, generateExportFilename } from '$utils/export.js';
  import StudyHeader from '$lib/StudyHeader.svelte';
  import Toolbar from '$lib/Toolbar.svelte';
  import Footer from '$lib/Footer.svelte';
  import Pagination from '$lib/Pagination.svelte';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { faRotate, faSearch, faCircleXmark } from '@fortawesome/pro-light-svg-icons';
  const browser = typeof window !== 'undefined';

  const API_BASE_URL = '/api/proxy';

  let periodStats = [];
  let resultCount = 0;
  let totalPages = 1;
  let currentPage = 1;
  let limit = 50;
  let isLoading = true;
  let isExporting = false;
  let hasError = false;
  let errorMessage = '';
  let searchText = '';
  let selectedType = 'ALL';

  let currentFetch = null;
  let isInitialized = false;

  // Gate StudyProperties fetch on auth readiness. The reactive statement
  // otherwise fires the moment $selectedStudy hydrates from localStorage,
  // which can be before installAuthFetch has an Auth0 token to attach —
  // the unauth'd request returns an HTML 401 page and response.json() throws.
  $: if ($selectedStudy?._id && $isAuthenticated) {
    fetchStudyProperties($selectedStudy._id);
  } else if (!$selectedStudy?._id) {
    studyProperties.set(null);
  }

  $: thresholds = $studyProperties?.glucoseParameters?.thresholds || {
    veryLow: 54,
    low: 70,
    high: 180,
    veryHigh: 250
  };

  $: currentUnit = $glucoseunit;

  $: featureDescription = `This report shows CGM participant statistics calculated separately for the Run-in (Visit 4-6) and End-point (Visit 28-32) periods.
  Statistics include time-in-range metrics, mean/SD, CV%, GMI, and completeness based on the number of expected readings during each period.
  
  *TIR* (Time in Range): % of readings ${formatThreshold(thresholds.low, currentUnit)}-${formatThreshold(thresholds.high, currentUnit)} ${currentUnit}
  *TAR* (Time Above Range): % of readings >${formatThreshold(thresholds.high, currentUnit)} ${currentUnit}
  *TBR* (Time Below Range): % of readings <${formatThreshold(thresholds.low, currentUnit)} ${currentUnit}
  `;

  onDestroy(() => {
    if (currentFetch) currentFetch.abort();
  });

  // Gate the data fetch on auth readiness for the same reason as the
  // StudyProperties fetch above — $selectedStudy can hydrate from localStorage
  // before Auth0 reports authenticated, and the resulting unauth'd request
  // returns an HTML 401 that surfaces as a generic console.error here.
  $: if (browser && isInitialized && $selectedStudy && $isAuthenticated) {
    fetchPeriodStats().catch(err => {
      if (err?.name === 'AbortError') return;
      console.error('Error in store subscription:', err);
      hasError = true;
      errorMessage = err.message;
    });
  }

  onMount(async () => {
    if (!browser) return;

    try {
      selectedParticipant.set(null);

      const urlSearchText = $page.url.searchParams.get('search');
      if (urlSearchText) {
        searchText = urlSearchText;
      }

      if (!$selectedStudy) {
        goto('/studies');
        return;
      }

      isInitialized = true;
    } catch (err) {
      console.error('Error in onMount:', err);
      hasError = true;
      errorMessage = err.message;
    }
  });

  function formatThreshold(value, unit) {
    return convertGlucoseValue(value, unit);
  }

  function formatPercent(value) {
    if (value === null || value === undefined) return '—';
    return `${value}%`;
  }

  function formatNumber(value, digits = 1) {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
    return Number(value).toFixed(digits);
  }

  function formatDateRange(startDate, endDate, timezoneCode = 'UTC') {
    if (!startDate || !endDate) return '—';
    const start = new Date(startDate);
    const end = new Date(endDate);
    const options = { month: 'short', day: 'numeric', timeZone: timezoneCode };
    return `${start.toLocaleDateString('en-US', options)} - ${end.toLocaleDateString('en-US', options)}`;
  }

  function formatPeriodRange(startDate, endDate, isComplete, timezoneCode = 'UTC') {
    if (!startDate) return '—';
    const start = new Date(startDate);
    const options = { month: 'short', day: 'numeric', timeZone: timezoneCode };
    const startLabel = start.toLocaleDateString('en-US', options);
    if (isComplete === false || !endDate) {
      return `${startLabel} - `;
    }
    const end = new Date(endDate);
    return `${startLabel} - ${end.toLocaleDateString('en-US', options)}`;
  }

  function createAbortController() {
    if (currentFetch) currentFetch.abort();
    return new AbortController();
  }

  function buildPeriodStatsUrl(pageNumber, pageSizeNumber) {
    const params = new URLSearchParams();
    params.set('page', pageNumber);
    params.set('pageSize', pageSizeNumber);
    params.set('studyGUID', $selectedStudy._id);
    params.set('searchText', searchText);
    params.set('subjectStatus', selectedType);
    params.set('inRange', 'true');

    if ($selectedSite?._id) {
      params.set('siteGUID', $selectedSite._id);
    }

    return `${API_BASE_URL}/complianceReport/periodStatistics?${params.toString()}`;
  }

  async function fetchPeriodStats() {
    const abortController = createAbortController();
    currentFetch = abortController;

    if (!$selectedStudy || !$selectedStudy._id) {
      periodStats = [];
      resultCount = 0;
      totalPages = 1;
      return;
    }

    try {
      isLoading = true;
      hasError = false;
      errorMessage = '';

      const url = buildPeriodStatsUrl(currentPage, limit);
      const res = await fetch(url, { signal: abortController.signal });

      if (currentFetch !== abortController) return;

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(`Failed to fetch period statistics: ${res.status} ${errorData.message || ''}`);
      }

      const responseData = await res.json();
      periodStats = responseData.data || [];
      resultCount = responseData.totalCount || 0;
      totalPages = responseData.totalPages || 1;
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('Error fetching period statistics:', err);
      periodStats = [];
      resultCount = 0;
      totalPages = 1;
      hasError = true;
      errorMessage = err.message || 'Failed to load period statistics.';
    } finally {
      if (currentFetch === abortController) {
        isLoading = false;
        currentFetch = null;
      }
    }
  }

  function handleRefresh() {
    fetchPeriodStats();
  }

  function handlePageChange(newPage) {
    if (newPage > 0 && newPage <= totalPages) {
      currentPage = newPage;
      fetchPeriodStats();
    }
  }

  function handleSearch() {
    const url = new URL(window.location);
    if (searchText) {
      url.searchParams.set('search', searchText);
    } else {
      url.searchParams.delete('search');
    }
    goto(url.toString(), { replaceState: true });
    currentPage = 1;
    fetchPeriodStats();
  }

  function handleClearSearch() {
    searchText = '';
    handleSearch();
  }

  function handleParticipantClick(item) {
    if (item.siteGUID) {
      const site = {
        _id: item.siteGUID,
        siteId: item.siteId,
        country: item.country
      };
      selectedSite.set(site);
    }
    selectedParticipant.set({ _id: item._id, subjectId: item.subjectId });
    goto(`/person`);
  }

  async function handleExportExcel() {
    try {
      isExporting = true;

      if (!$selectedStudy || !$selectedStudy._id) {
        throw new Error('No study selected');
      }

      const url = buildPeriodStatsUrl(1, 999999);
      const res = await fetch(url);

      if (!res.ok) {
        throw new Error('Failed to fetch period statistics for export');
      }

      const responseData = await res.json();
      const allData = responseData.data || [];

      const filename = generateExportFilename({
        protocolNumber: $selectedStudy?.protocolNumber,
        siteId: $selectedSite?.siteId,
        reportType: 'Period_Statistics'
      });

      const formattedData = allData.map((item) => ({
        Protocol: $selectedStudy?.protocolNumber || '',
        'Site ID': item.siteId || '',
        Country: item.country || '',
        Participant: item.subjectId || '',
        Status: item.statusLabel || '',
        'Run-in Period': formatPeriodRange(item.runIn?.startDate, item.runIn?.endDate, item.runIn?.isComplete, item.timezoneCode),
        'Run-in Readings': item.runIn?.stats?.total ?? '',
        'Run-in Days': item.runIn?.totalDays ?? '',
        'Run-in Sensors': item.runIn?.stats?.sensorCount ?? '',
        'Run-in Comp (%)': item.runIn?.stats?.completenessPercent ?? '',
        'Run-in Mean': convertGlucoseValue(item.runIn?.stats?.mean, $glucoseunit)?.toFixed(1) || '',
        'Run-in Unit': $glucoseunit,
        'Run-in GMI (%)': item.runIn?.stats?.gmi ?? '',
        'Run-in SD': convertGlucoseValue(item.runIn?.stats?.sd, $glucoseunit)?.toFixed(1) || '',
        'Run-in CV (%)': item.runIn?.stats?.cvPercent ?? '',
        'Run-in TBR2 (%)': item.runIn?.stats?.veryLow?.percent ?? '',
        'Run-in TBR1 (%)': item.runIn?.stats?.low?.percent ?? '',
        'Run-in TBR (%)': item.runIn?.stats?.tbrCombined ?? '',
        'Run-in TIR (%)': item.runIn?.stats?.target?.percent ?? '',
        'Run-in TAR (%)': item.runIn?.stats?.tarCombined ?? '',
        'Run-in TAR1 (%)': item.runIn?.stats?.high?.percent ?? '',
        'Run-in TAR2 (%)': item.runIn?.stats?.veryHigh?.percent ?? '',
        'End-point Period': formatPeriodRange(item.endPoint?.startDate, item.endPoint?.endDate, item.endPoint?.isComplete, item.timezoneCode),
        'End-point Readings': item.endPoint?.stats?.total ?? '',
        'End-point Days': item.endPoint?.totalDays ?? '',
        'End-point Sensors': item.endPoint?.stats?.sensorCount ?? '',
        'End-point Comp (%)': item.endPoint?.stats?.completenessPercent ?? '',
        'End-point Mean': convertGlucoseValue(item.endPoint?.stats?.mean, $glucoseunit)?.toFixed(1) || '',
        'End-point Unit': $glucoseunit,
        'End-point GMI (%)': item.endPoint?.stats?.gmi ?? '',
        'End-point SD': convertGlucoseValue(item.endPoint?.stats?.sd, $glucoseunit)?.toFixed(1) || '',
        'End-point CV (%)': item.endPoint?.stats?.cvPercent ?? '',
        'End-point TBR2 (%)': item.endPoint?.stats?.veryLow?.percent ?? '',
        'End-point TBR1 (%)': item.endPoint?.stats?.low?.percent ?? '',
        'End-point TBR (%)': item.endPoint?.stats?.tbrCombined ?? '',
        'End-point TIR (%)': item.endPoint?.stats?.target?.percent ?? '',
        'End-point TAR (%)': item.endPoint?.stats?.tarCombined ?? '',
        'End-point TAR1 (%)': item.endPoint?.stats?.high?.percent ?? '',
        'End-point TAR2 (%)': item.endPoint?.stats?.veryHigh?.percent ?? ''
      }));

      await exportSensorStatisticsToExcel(formattedData, filename);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
    } finally {
      isExporting = false;
    }
  }
</script>

{#if hasError}
  <div class="p-4 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-100 rounded-lg mb-4">
    <p>An error occurred: {errorMessage}</p>
    <button
      class="mt-2 px-4 py-2 bg-red-200 dark:bg-red-800 rounded hover:bg-red-300 dark:hover:bg-red-700"
      on:click={() => {
        hasError = false;
        errorMessage = '';
        if ($selectedStudy) fetchPeriodStats();
      }}
    >
      Try Again
    </button>
  </div>
{/if}

<StudyHeader />

<FeatureInfo
  title="Period Statistics Report"
  description={featureDescription}
  marginBottom="mb-0"
/>

{#if $selectedStudy}
  <StudyDetails showOptionsMenu={false} />
{/if}

<Toolbar title="Period Statistics Report" itemCountTitle="Participants" itemCount={resultCount}>
  <div class="flex items-center gap-4">
    <div class="flex items-center gap-2">
      <div class="relative">
        <input
          type="text"
          placeholder="Search"
          bind:value={searchText}
          on:keydown={(e) => e.key === 'Enter' && handleSearch()}
          class="px-3 py-1.5 text-xs text-gray-800 dark:text-gray-100 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 pr-8"
        />
        {#if searchText}
          <button
            on:click={handleClearSearch}
            class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            title="Clear search"
          >
            <FontAwesomeIcon icon={faCircleXmark} />
          </button>
        {/if}
      </div>
      <button on:click={handleSearch} title="Search">
        <FontAwesomeIcon icon={faSearch} />
      </button>
    </div>
    <div class="flex items-baseline gap-2">
      Status:
      <select
        bind:value={selectedType}
        on:change={() => {
          currentPage = 1;
          fetchPeriodStats();
        }}
        class="form-select h-6 border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
      >
        <option value="ALL" selected>All</option>
        <option value="ACTIVE">Active</option>
        <option value="COMPD">End of Participation</option>
      </select>
    </div>
    <button on:click={handleRefresh} class="pagination mx-1 p-0" title="Refresh">
      <FontAwesomeIcon icon={faRotate} />
    </button>
  </div>
</Toolbar>

<div class="px-1 pt-0">
  {#if isLoading}
    <div class="flex justify-center items-center py-8">
      <div class="loader"></div>
    </div>
  {:else if periodStats.length > 0}
    <div class="overflow-x-auto">
      <table class="min-w-full border-collapse table-auto">
        <thead>
          <tr class="text-sm text-black dark:text-slate-300 bg-stone-200/50 dark:bg-gray-800/90">
            <th class="px-3 py-2 text-left bold sticky left-0 z-20 bg-stone-200 dark:bg-gray-800 min-w-[60px]" rowspan="2">Site</th>
            <th class="px-3 py-2 text-left bold sticky left-[60px] z-20 bg-stone-200 dark:bg-gray-800 min-w-[80px] shadow-[2px_0_5px_-2px_rgba(0,0,0,0.15)]" rowspan="2">Participant</th>
            <th class="px-3 py-2 text-left bold" rowspan="2">Country</th>
            <th class="px-3 py-2 text-left bold" rowspan="2">Status</th>
            <th class="px-3 py-2 text-left bold" rowspan="2">Run-in Period</th>
            <th class="px-3 py-2 text-left bold" colspan="15">Run-in Stats (Visit 4-6)</th>
            <th class="px-3 py-2 text-left bold" rowspan="2">End-point Period</th>
            <th class="px-3 py-2 text-left bold" colspan="15">End-point Stats (Visit 28-32)</th>
          </tr>
          <tr class="text-xs text-black dark:text-slate-300 bg-stone-100/60 dark:bg-gray-800/70">
            <th class="px-2 py-1 text-left">Readings</th>
            <th class="px-2 py-1 text-left">Days</th>
            <th class="px-2 py-1 text-left">Sensors</th>
            <th class="px-2 py-1 text-left">Comp.</th>
            <th class="px-2 py-1 text-left">Mean</th>
            <th class="px-2 py-1 text-left">GMI</th>
            <th class="px-2 py-1 text-left">SD</th>
            <th class="px-2 py-1 text-left">CV%</th>
            <th class="px-2 py-1 text-left">TBR2</th>
            <th class="px-2 py-1 text-left">TBR1</th>
            <th class="px-2 py-1 text-left bg-amber-100 dark:bg-amber-900/30">TBR</th>
            <th class="px-2 py-1 text-left bg-green-100 dark:bg-green-900/30">TIR</th>
            <th class="px-2 py-1 text-left bg-red-100 dark:bg-red-900/30">TAR</th>
            <th class="px-2 py-1 text-left">TAR1</th>
            <th class="px-2 py-1 text-left">TAR2</th>
            <th class="px-2 py-1 text-left">Readings</th>
            <th class="px-2 py-1 text-left">Days</th>
            <th class="px-2 py-1 text-left">Sensors</th>
            <th class="px-2 py-1 text-left">Comp.</th>
            <th class="px-2 py-1 text-left">Mean</th>
            <th class="px-2 py-1 text-left">GMI</th>
            <th class="px-2 py-1 text-left">SD</th>
            <th class="px-2 py-1 text-left">CV%</th>
            <th class="px-2 py-1 text-left">TBR2</th>
            <th class="px-2 py-1 text-left">TBR1</th>
            <th class="px-2 py-1 text-left bg-amber-100 dark:bg-amber-900/30">TBR</th>
            <th class="px-2 py-1 text-left bg-green-100 dark:bg-green-900/30">TIR</th>
            <th class="px-2 py-1 text-left bg-red-100 dark:bg-red-900/30">TAR</th>
            <th class="px-2 py-1 text-left">TAR1</th>
            <th class="px-2 py-1 text-left">TAR2</th>
          </tr>
        </thead>
        <tbody>
          {#each periodStats as item, index (item._id)}
            <tr
              class="text-sm hover:bg-white dark:hover:bg-gray-775 text-gray-800 dark:text-slate-300 {index %
                2 ===
              0
                ? 'bg-stone-50 dark:bg-gray-725'
                : 'bg-stone-75 dark:bg-gray-750'}"
            >
              <td class="px-3 py-2 sticky left-0 z-20 min-w-[60px] {index % 2 === 0 ? 'bg-stone-50 dark:bg-gray-725' : 'bg-stone-75 dark:bg-gray-750'}">{item.siteId || ''}</td>
              <td
                class="px-3 py-2 whitespace-nowrap sticky left-[60px] z-20 min-w-[80px] cursor-pointer hover:underline {index % 2 === 0 ? 'bg-stone-50 dark:bg-gray-725' : 'bg-stone-75 dark:bg-gray-750'} shadow-[2px_0_5px_-2px_rgba(0,0,0,0.15)]"
                on:click={() => handleParticipantClick(item)}
              >
                {item.subjectId || ''}
              </td>
              <td class="px-3 py-2">{item.country || ''}</td>
              <td class="px-3 py-2">
                <span
                  class="inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset"
                  class:text-green-600={item.statusLabel === 'Enrolled' || item.statusLabel === 'Screened/Enrolled'}
                  class:ring-green-600={item.statusLabel === 'Enrolled' || item.statusLabel === 'Screened/Enrolled'}
                  class:text-gray-600={item.statusLabel === 'Discontinued'}
                  class:ring-gray-600={item.statusLabel === 'Discontinued'}
                  class:text-sky-600={item.statusLabel === 'Completed'}
                  class:ring-sky-600={item.statusLabel === 'Completed'}
                >
                  {item.statusLabel || ''}
                </span>
              </td>
              <td class="px-3 py-2 text-xs whitespace-nowrap">
                {formatPeriodRange(item.runIn?.startDate, item.runIn?.endDate, item.runIn?.isComplete, item.timezoneCode)}
              </td>
              <td class="px-2 py-1">{item.runIn?.stats?.total ?? '—'}</td>
              <td class="px-2 py-1">{item.runIn?.totalDays ?? '—'}</td>
              <td class="px-2 py-1">{item.runIn?.stats?.sensorCount ?? '—'}</td>
              <td class="px-2 py-1">{formatPercent(item.runIn?.stats?.completenessPercent)}</td>
              <td class="px-2 py-1 whitespace-nowrap">{formatNumber(convertGlucoseValue(item.runIn?.stats?.mean, $glucoseunit), 1)} {$glucoseunit}</td>
              <td class="px-2 py-1">{formatNumber(item.runIn?.stats?.gmi, 2)}%</td>
              <td class="px-2 py-1 whitespace-nowrap">{formatNumber(convertGlucoseValue(item.runIn?.stats?.sd, $glucoseunit), 1)} {$glucoseunit}</td>
              <td class="px-2 py-1">{formatPercent(item.runIn?.stats?.cvPercent)}</td>
              <td class="px-2 py-1">{formatPercent(item.runIn?.stats?.veryLow?.percent)}</td>
              <td class="px-2 py-1">{formatPercent(item.runIn?.stats?.low?.percent)}</td>
              <td class="px-2 py-1 bg-amber-50 dark:bg-amber-900/20">{formatPercent(item.runIn?.stats?.tbrCombined)}</td>
              <td class="px-2 py-1 bg-green-50 dark:bg-green-900/20 font-medium">{formatPercent(item.runIn?.stats?.target?.percent)}</td>
              <td class="px-2 py-1 bg-red-50 dark:bg-red-900/20 font-medium">{formatPercent(item.runIn?.stats?.tarCombined)}</td>
              <td class="px-2 py-1">{formatPercent(item.runIn?.stats?.high?.percent)}</td>
              <td class="px-2 py-1">{formatPercent(item.runIn?.stats?.veryHigh?.percent)}</td>
              <td class="px-3 py-2 text-xs whitespace-nowrap">
                {formatPeriodRange(item.endPoint?.startDate, item.endPoint?.endDate, item.endPoint?.isComplete, item.timezoneCode)}
              </td>
              <td class="px-2 py-1">{item.endPoint?.stats?.total ?? '—'}</td>
              <td class="px-2 py-1">{item.endPoint?.totalDays ?? '—'}</td>
              <td class="px-2 py-1">{item.endPoint?.stats?.sensorCount ?? '—'}</td>
              <td class="px-2 py-1">{formatPercent(item.endPoint?.stats?.completenessPercent)}</td>
              <td class="px-2 py-1 whitespace-nowrap">{formatNumber(convertGlucoseValue(item.endPoint?.stats?.mean, $glucoseunit), 1)} {$glucoseunit}</td>
              <td class="px-2 py-1">{formatNumber(item.endPoint?.stats?.gmi, 2)}%</td>
              <td class="px-2 py-1 whitespace-nowrap">{formatNumber(convertGlucoseValue(item.endPoint?.stats?.sd, $glucoseunit), 1)} {$glucoseunit}</td>
              <td class="px-2 py-1">{formatPercent(item.endPoint?.stats?.cvPercent)}</td>
              <td class="px-2 py-1">{formatPercent(item.endPoint?.stats?.veryLow?.percent)}</td>
              <td class="px-2 py-1">{formatPercent(item.endPoint?.stats?.low?.percent)}</td>
              <td class="px-2 py-1 bg-amber-50 dark:bg-amber-900/20">{formatPercent(item.endPoint?.stats?.tbrCombined)}</td>
              <td class="px-2 py-1 bg-green-50 dark:bg-green-900/20 font-medium">{formatPercent(item.endPoint?.stats?.target?.percent)}</td>
              <td class="px-2 py-1 bg-red-50 dark:bg-red-900/20 font-medium">{formatPercent(item.endPoint?.stats?.tarCombined)}</td>
              <td class="px-2 py-1">{formatPercent(item.endPoint?.stats?.high?.percent)}</td>
              <td class="px-2 py-1">{formatPercent(item.endPoint?.stats?.veryHigh?.percent)}</td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>
  {:else}
    <div class="text-center py-8 text-gray-500 dark:text-gray-400">
      No participants found
    </div>
  {/if}
</div>

<Footer>
  <Pagination {currentPage} {totalPages} onPageChange={handlePageChange} />
  <div>
    <button
      on:click={handleExportExcel}
      class="bg-stone-300 dark:bg-gray-600 text-black dark:text-white py-1 px-4 rounded-full text-xs"
      >Excel</button
    >
  </div>
</Footer>

{#if isExporting}
  <div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div class="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-lg flex flex-col items-center">
      <div class="loader mb-4"></div>
      <p class="text-sm text-gray-700 dark:text-gray-300">Preparing Excel export...</p>
    </div>
  </div>
{/if}

<style>
  .loader {
    border: 2px solid #20669c;
    border-radius: 50%;
    border-top: 4px solid #98acb4;
    width: 32px;
    height: 32px;
    animation: spin 2s linear infinite;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  .overflow-x-auto::-webkit-scrollbar {
    display: none;
  }

  .overflow-x-auto {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
</style>
